import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Trophy, Calendar, Target, MapPin, Percent, ArrowRight, Tent, Utensils, Users, Award, DollarSign, Mail, Phone, Link, CircleDollarSign } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface ComingSoonAlertProps {
  huntType?: string;
}
export const ComingSoonAlert: React.FC<ComingSoonAlertProps> = ({
  huntType
}) => {
  if (huntType === "elk") {
    return <div className="space-y-8">
        {/* Overview Card */}
        <Card className="border border-tribal-neutral/20 shadow-sm">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-tribal-secondary">Elk Hunt Overview</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <Calendar className="h-5 w-5 text-tribal-primary mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm text-tribal-neutral">Season Dates</p>
                      <p className="font-medium">Sep 1, 2025 - Oct 31, 2025</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Percent className="h-5 w-5 text-tribal-primary mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm text-tribal-neutral">Success Rate</p>
                      <p className="font-medium">85%</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-tribal-primary mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm text-tribal-neutral">Hunting Area</p>
                      <p className="font-medium">Duck Valley Reservation</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Target className="h-5 w-5 text-tribal-primary mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm text-tribal-neutral">Hunt Type</p>
                      <p className="font-medium">Guided</p>
                    </div>
                  </div>
                </div>
                
                <p className="text-tribal-secondary mt-4">
                  Guided elk hunts in prime elk habitat with experienced tribal guides. 
                  Available for rifle, bow, or muzzleloader.
                </p>
              </div>
              
              <div className="bg-tribal-light rounded-lg p-4 border border-tribal-neutral/10">
                <h4 className="text-lg font-semibold text-tribal-secondary mb-4">Permit Pricing</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-tribal-neutral/10">
                    <div className="flex items-center">
                      <Trophy className="h-5 w-5 text-amber-500 mr-2" />
                      <span className="font-medium">Bull Elk Tag</span>
                    </div>
                    <div className="font-bold text-tribal-secondary">
                      $25,000
                      <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Limited</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pb-2 border-b border-tribal-neutral/10">
                    <div className="flex items-center">
                      <Trophy className="h-5 w-5 text-amber-500 mr-2" />
                      <span className="font-medium">Cow Elk Tag</span>
                    </div>
                    <div className="font-bold text-tribal-secondary">
                      $3,000
                      <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">Sold Out</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-blue-500 mr-2" />
                      <span className="font-medium">Non-Hunter Ride-Along</span>
                    </div>
                    <div className="font-bold text-tribal-secondary">
                      $500<span className="text-sm font-normal">/day</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Purchase Options */}
        <Card className="border border-tribal-neutral/20 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-2xl font-bold text-tribal-secondary mb-4">Permit Purchase Options</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-tribal-light rounded-lg p-4 border border-tribal-neutral/10">
                <div className="flex items-center mb-3">
                  <CircleDollarSign className="h-6 w-6 text-tribal-primary mr-2" />
                  <h4 className="font-semibold text-lg">Outright Purchase</h4>
                </div>
                <p className="text-tribal-secondary mb-3">
                  Immediate guaranteed permit access to bull elk hunting.
                </p>
                <div className="font-bold text-xl text-tribal-secondary mt-3">$25,000</div>
                <Button className="w-full mt-4 bg-tribal-primary hover:bg-tribal-primary/90">
                  Reserve Now <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
              
              
            </div>
          </CardContent>
        </Card>
        
        {/* Exclusive Advantages */}
        <Card className="border border-tribal-neutral/20 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-2xl font-bold text-tribal-secondary mb-4">Exclusive Advantages</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                    <MapPin className="h-4 w-4 text-tribal-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Exclusive Access</p>
                    <p className="text-sm text-tribal-neutral">
                      Hunt in Nevada (Units 061 & 071), Idaho lands, and restricted tribal areas.
                    </p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                    <Users className="h-4 w-4 text-tribal-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Expert Guides</p>
                    <p className="text-sm text-tribal-neutral">
                      Guides track herd movements, ensuring legal and maximized hunting opportunities.
                    </p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                    <Target className="h-4 w-4 text-tribal-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Flexible Weapon Use</p>
                    <p className="text-sm text-tribal-neutral">
                      Use rifle, archery, or muzzleloader daily based on your preference.
                    </p>
                  </div>
                </li>
              </ul>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                    <Calendar className="h-4 w-4 text-tribal-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Prime Hunting Dates</p>
                    <p className="text-sm text-tribal-neutral">
                      Hunt during peak elk rutting season for optimal opportunities.
                    </p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                    <ArrowRight className="h-4 w-4 text-tribal-primary" />
                  </div>
                  <div>
                    <p className="font-medium">ATV Transportation</p>
                    <p className="text-sm text-tribal-neutral">
                      Provided for your hunt (option to bring personal ATV).
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
        
        {/* Camp & Accommodations */}
        <Card className="border border-tribal-neutral/20 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-2xl font-bold text-tribal-secondary mb-4">Camp & Accommodations</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                      <Tent className="h-4 w-4 text-tribal-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Traditional Teepee Camping</p>
                      <p className="text-sm text-tribal-neutral">
                        Accommodation ready upon arrival with all necessities.
                      </p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                      <Utensils className="h-4 w-4 text-tribal-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Meals Provided</p>
                      <p className="text-sm text-tribal-neutral">
                        Hearty and homestyle meals prepared by onsite camp cook.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                      <MapPin className="h-5 w-5 text-tribal-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-tribal-neutral">Airport Pickup</p>
                      <p className="font-medium">Optional from Boise, ID (additional fee)</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="bg-tribal-primary/10 rounded-full p-1 mr-3 mt-0.5">
                      <DollarSign className="h-5 w-5 text-tribal-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-tribal-neutral">Lodging Options</p>
                      <p className="font-medium">Available pre- and post-hunt lodging in Owyhee, NV (~$90/night)</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Why Hunt Here */}
        <Card className="border border-tribal-neutral/20 shadow-sm bg-tribal-primary/5">
          <CardContent className="p-6">
            <h3 className="text-2xl font-bold text-tribal-secondary mb-4">Why Hunt Here?</h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="bg-tribal-primary/10 rounded-full p-2 w-12 h-12 flex items-center justify-center mb-3">
                  <Users className="h-6 w-6 text-tribal-primary" />
                </div>
                <h4 className="font-semibold text-lg mb-2">Cultural Experience</h4>
                <p className="text-sm text-tribal-neutral">
                  Immerse yourself in a cultural, historical, and conservation-focused hunting experience.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="bg-tribal-primary/10 rounded-full p-2 w-12 h-12 flex items-center justify-center mb-3">
                  <Trophy className="h-6 w-6 text-tribal-primary" />
                </div>
                <h4 className="font-semibold text-lg mb-2">High-Quality Game</h4>
                <p className="text-sm text-tribal-neutral">
                  Enjoy minimal hunting pressure and high-quality game in pristine wilderness.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="bg-tribal-primary/10 rounded-full p-2 w-12 h-12 flex items-center justify-center mb-3">
                  <Award className="h-6 w-6 text-tribal-primary" />
                </div>
                <h4 className="font-semibold text-lg mb-2">Sustainable Hunting</h4>
                <p className="text-sm text-tribal-neutral">
                  Participate in respectful and sustainable hunting practices aligned with Shoshone-Paiute traditions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Support a Worthy Cause */}
        <Card className="border border-amber-200 shadow-sm bg-amber-50">
          
        </Card>
        
        {/* Booking & Contact Information */}
        <Card className="border border-tribal-neutral/20 shadow-sm">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-2xl font-bold text-tribal-secondary mb-4">Booking Information</h3>
                <p className="mb-6 text-tribal-neutral">
                  Ready to experience a hunt of a lifetime? Book your elk hunt today or contact us for more information.
                </p>
                
                <Button className="w-full md:w-auto bg-tribal-primary hover:bg-tribal-primary/90 text-lg py-6">
                  Reserve Your Hunt <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
              
              <div className="bg-tribal-light rounded-lg p-5 border border-tribal-neutral/10">
                <h4 className="font-semibold text-tribal-secondary text-lg mb-4">Contact Us</h4>
                
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <Mail className="h-5 w-5 text-tribal-primary mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm text-tribal-neutral">Email</p>
                      <p className="font-medium">tribalelkhunts@shopai.org</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <Phone className="h-5 w-5 text-tribal-primary mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm text-tribal-neutral">Phone</p>
                      <p className="font-medium">(208) 845-2377</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <Link className="h-5 w-5 text-tribal-primary mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm text-tribal-neutral">Website</p>
                      <p className="font-medium">www.shopaitribes.org</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>;
  } else {
    return <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
        <h3 className="text-xl font-bold text-amber-800 mb-2">Coming Soon</h3>
        <p className="text-amber-700">
          Packages for this hunt type will be available soon. Please check back later or contact us for more information.
        </p>
      </div>;
  }
};